import { IonButton, IonCard, IonCardContent, IonInput, IonItem, IonLabel, IonLoading, IonRadio, IonRadioGroup } from '@ionic/react'
import './modify-profile.scss'
import BasePage from 'pages/base-page/base-page.page'
import { useTranslation } from 'react-i18next'
import { useCitizenship, useProfileInfo, useProfileTypes } from 'hooks'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useEffect, useState } from 'react'
import { profileService, routeService } from 'services'
import { Redirect } from 'react-router'
import i18next from 'i18n'
import Select from 'react-select'
import { Link } from 'react-router-dom'

const validationSchema = Yup.object().shape({
  userprofile: Yup.string().required(i18next.t('form_validator.required')),
  phone: Yup.string().required(i18next.t('form_validator.required')),
  citizenship: Yup.string().required(i18next.t('form_validator.required')),
  mail: Yup.string().email(i18next.t('form_validator.error_mail')).required(i18next.t('form_validator.required')),
  confirm_mail: Yup.string()
    .oneOf([Yup.ref('mail'), ''], i18next.t('form_validator.match_mail'))
    .required(i18next.t('form_validator.required')),
})

const ModifyProfile: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [redirect, setRedirect] = useState<string | undefined>(undefined)
  const [isEmailModified, setIsEmailModified] = useState(false)

  const profileTypes = useProfileTypes()
  const profileInfo = useProfileInfo()
  const citizenship = useCitizenship()
  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('T')[0].split('-')
    return `${day}/${month}/${year}`
  }

  const { t } = useTranslation()
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      lastname: profileInfo?.cognome,
      name: profileInfo?.nome,
      birth_date: formatDate(profileInfo?.dataDiNascita!),
      gender: profileInfo?.sesso,
      country: profileInfo?.provinciaDiNascita,
      place: profileInfo?.comuneDiNascita,
      citizenship: citizenship.find((c) => c.descrizione === profileInfo?.cittadinanza)?.id || '',
      userprofile: profileInfo?.tipoProfilo,
      mail: profileInfo?.email,
      phone: profileInfo?.telefono,
      fiscalcode: profileInfo?.codiceFiscale,
      confirm_mail: profileInfo?.email,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setLoading(true)
      profileService.modifyProfile(
        values.userprofile,
        values.phone,
        values.mail,
        values.citizenship,
        () => {
          setLoading(false)
          setRedirect(routeService.generateRoutePath('/'))
        },
        () => {
          setLoading(false)
          setRedirect(routeService.generateRoutePath('/error'))
        },
      )
    },
  })
  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        formik.handleSubmit()
      }
    })
  })

  if (!!redirect) {
    return <Redirect to={redirect}></Redirect>
  }

  return (
    <BasePage pageClass="edisu-page-modify-profile" bgColor="#EDF2F5">
      <IonLoading isOpen={loading} message={t('loading')} />
      <div className="edisu-modify-profile-grid">
        <h2> {t('modify_profile')}</h2>
        <IonCard className="edisu-modify-profile-card">
          <IonCardContent>
            <form onSubmit={formik.handleSubmit} className="container">
              <div className="row">
                <div className="field">
                  <IonLabel className="form-label">{t('lastname').toUpperCase()}</IonLabel>
                  <IonInput disabled={true} value={formik.values.lastname} className="input disabled" name="lastname"></IonInput>
                </div>

                <div className="field">
                  <IonLabel className={'form-label'}>{t('name').toUpperCase()}</IonLabel>
                  <IonInput disabled={true} value={formik.values.name} className="input disabled" name="name"></IonInput>
                </div>
              </div>
              <div className="row">
                <div className="field date">
                  <IonLabel className={'form-label'}>{t('birth_date').toUpperCase()}</IonLabel>
                  <br />
                  <input disabled={true} value={formik.values.birth_date} name="birth_date" className="datepicker disabled"></input>
                </div>

                <div className="field">
                  <IonLabel className="form-label">{t('gender').toUpperCase()}</IonLabel>
                  <IonRadioGroup value={formik.values.gender} className="radioGroup">
                    <IonItem lines="none" className="itemMF">
                      <IonLabel className="labelMF">M</IonLabel>
                      <IonRadio disabled={true} value="M"></IonRadio>
                    </IonItem>
                    <IonItem lines="none" className="itemMF">
                      <IonLabel className="labelMF">F</IonLabel>
                      <IonRadio disabled={true} value="F"></IonRadio>
                    </IonItem>
                  </IonRadioGroup>
                </div>
              </div>

              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('birth_country').toUpperCase()}</IonLabel>
                  <Select
                    name="country"
                    className="dropdown"
                    isDisabled={true}
                    placeholder={formik.values.country}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                </div>
                <div className="field">
                  <IonLabel className={'form-label'}>{t('birthplace').toUpperCase()}</IonLabel>
                  <Select
                    name="place"
                    className="dropdown"
                    isDisabled={true}
                    placeholder={formik.values.place}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('fiscalcode').toUpperCase()}</IonLabel>
                  <IonInput disabled={true} value={formik.values.fiscalcode} name="fiscalcode" className="input disabled"></IonInput>
                </div>
                <div className="field">
                  <IonLabel className={'form-label'}>{t('phone_number').toUpperCase()}</IonLabel>
                  <IonInput
                    value={formik.values.phone}
                    onIonInput={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="phone"
                    className="input"
                    placeholder={t('your_phone')}
                  ></IonInput>
                  <IonLabel className={formik.touched.phone && formik.errors.phone ? 'form-label-error' : 'hidden'}>
                    {formik.touched.phone && formik.errors.phone ? formik.errors.phone : t('phone_number')}
                  </IonLabel>
                </div>
              </div>
              <div className="row">
                <div className="field">
                  <IonLabel className={'form-label'}>{t('citizenship').toUpperCase()}</IonLabel>
                  <Select
                    name="citizenship"
                    options={
                      citizenship
                        ? citizenship.map((c) => ({
                            value: c.id,
                            label: c.descrizione,
                          }))
                        : []
                    }
                    onChange={(e) => {
                      formik.setFieldValue('citizenship', e!.value)
                    }}
                    onBlur={(e) => {
                      formik.touched.citizenship = true
                      formik.handleBlur(e)
                    }}
                    className="dropdown"
                    placeholder={profileInfo?.cittadinanza}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                  <IonLabel className={formik.touched.citizenship && formik.errors.citizenship ? 'form-label-error' : 'hidden'}>
                    {formik.touched.citizenship && formik.errors.citizenship ? formik.errors.citizenship : t('citizenship')}
                  </IonLabel>
                </div>

                <div className="field">
                  <IonLabel className={'form-label'}>{t('user_profile').toUpperCase()}</IonLabel>
                  <Select
                    name="userprofile"
                    options={
                      profileTypes
                        ? profileTypes.map((c) => ({
                            value: c.id,
                            label: c.descrizione.toUpperCase(),
                          }))
                        : []
                    }
                    onChange={(e) => {
                      formik.setFieldValue('userprofile', e!.value.toString())
                    }}
                    className="dropdown"
                    placeholder={profileTypes.find((i) => (i.id as string) === formik.values.userprofile)?.descrizione.toUpperCase()}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: 'lightgrey',
                        '&:hover': {
                          borderColor: '#eb445a',
                        },
                        boxShadow: 'none',
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#eb445a' : state.isFocused ? 'lightgrey' : 'white',
                        '&:hover': {
                          backgroundColor: 'lightgrey',
                          color: 'white',
                        },
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="field-email">
                  <IonLabel className={'form-label'}>{t('mail').toUpperCase()}</IonLabel>
                  <IonInput
                    type="email"
                    value={formik.values.mail}
                    onIonInput={(e) => {
                      formik.handleChange(e)
                      setIsEmailModified(e.detail.value !== profileInfo?.email)
                    }}
                    onBlur={formik.handleBlur}
                    name="mail"
                    className="input"
                    placeholder={t('your_mail')}
                    onCopy={(e) => {
                      e.preventDefault()
                      return false
                    }}
                  ></IonInput>
                  <IonLabel className={formik.touched.mail && formik.errors.mail ? 'form-label-error' : 'hidden'}>
                    {formik.touched.mail && formik.errors.mail ? formik.errors.mail : t('mail')}
                  </IonLabel>
                </div>
                {isEmailModified && (
                  <div className="field">
                    <IonLabel className={'form-label'}>{t('confirm_mail').toUpperCase()}</IonLabel>
                    <IonInput
                      type="email"
                      value={formik.values.confirm_mail}
                      onIonInput={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="confirm_mail"
                      className="input"
                      placeholder={t('confirm_your_mail')}
                      onPaste={(e) => {
                        e.preventDefault()
                        return false
                      }}
                    ></IonInput>
                    <IonLabel className={formik.touched.confirm_mail && formik.errors.confirm_mail ? 'form-label-error' : 'hidden'}>
                      {formik.touched.confirm_mail && formik.errors.confirm_mail ? formik.errors.confirm_mail : t('mail')}
                    </IonLabel>
                  </div>
                )}
              </div>

              <div className="buttonRegister">
                <Link to={routeService.generateRoutePath('/')} replace>
                  <IonButton>{t('go_back')}</IonButton>
                </Link>
                <IonButton type="submit">{t('modify')}</IonButton>
              </div>
            </form>
          </IonCardContent>
        </IonCard>
      </div>
    </BasePage>
  )
}

export default ModifyProfile
